import type {
  QMPricingResponse,
  QMProduct,
  BorrowerPaidDetail,
  AdjustmentGroup,
} from "./types.ts";
import { config } from "./config.ts";
import { debug } from "./debug.ts";
import { getStateConfigByAbbreviation } from "./state-config.ts";

// Define local interfaces for the client
interface PricingResult {
  baseRate: number;
  finalRate: number;
  adjustments: { name: string; value: number }[];
  alternativeProgram: string;
  totalPayment: number;
  loanType: string;
  amortizationType: string;
  loanTerm: string;
  lockPeriod: number;
  isFromFallback?: boolean;
  monthlyPayment?: {
    monthlyPI: number;
    total: number;
    mi: number;
  };
  borrowerPaid?: number;
  finalClosingCost?: number;
  apr?: number;
  borrowerPaidDetails?: BorrowerPaidDetail[];
  disqualified?: boolean;
  disqualifyAdjustments?: string[];
  /**
   * All adjustment details collected from groupAdjustmentProperties
   */
  allAdjustments?: { name: string; type: string; value: number }[];

  /**
   * Price as percent of par (e.g. 99.750 or 101.000), calculated as 100 - discountPoints.
   */
  price: number;
}

interface QMPricingRequest {
  companyId: string;
  criteria: {
    variableLoanTypes: string[];
    loanAmount: number;
    purchasePrice: number;
    appraisedValue: number;
    monthlyIncome: number;
    monthlyDebt: number;
    escrowWaiver: boolean;
    fico: number;
    firstTimeHomeBuyer: boolean;
    fundingFeeFinanced: "YES" | "NO" | "EXEMPT";
    ufmipFinanced: "YES" | "NO";
    guranteeFeeFinanced: "YES" | "NO";
    vaServiceType: "First_Use" | "Subsequent_Use";
    veteranType: string;
    loanPurpose: string;
    loanType: "Fixed" | "Variable";
    mortgageTypes: string[];
    pmiType: string;
    propertyUse: string;
  };
  loanPurposeCriteria: string[];
  loanTypeCriteria: ("Fixed" | "Variable")[];
  maxListingPerRate: string;
  miCriteria: {
    miCompanyName: string | null;
  };
  property: {
    address: {
      state: string;
      zip: string;
    };
    numberOfUnit: number;
    propertyType: string;
  };
  termsCriteria: string[];
  brokerCriteria: {
    ausList: string[];
    dayLocks: number;
  };
  accessCriteria: {
    userContext: null;
  };
  dynamicPropertiesMap: {
    [key: string]: {
      fieldId: string;
      value?: string;
    };
  };
}

// Function to map form values to QMPricingRequest
export function mapFormToQMPricingRequest(formValues: any): QMPricingRequest {
  // Calculate LTV
  const ltv = formValues.ltv! / 100;
  const loanAmount = formValues.loanAmount!;
  let appraisedValue = Math.ceil(loanAmount / ltv);

  // Map amortization type
  // const amortizationType = formValues.amortizationType.includes("fixed")
  //   ? "Fixed"
  //   : formValues.amortizationType.includes("arm")
  //   ? "AdjustableRate"
  //   : "OtherAmortizationType";
  const loanTerm = parseInt(formValues.amortizationType!.split("-")[0], 10)!;

  // Use the Loan Type input field directly
  const armVariants = ["30-7-6-ARM", "40-7-6-ARM", "0-10-6-ARM", "30-10-6-ARM"];
  const loanType: "Fixed" | "Variable" = armVariants.includes(
    formValues.amortizationType
  )
    ? "Variable"
    : formValues.loanType === "Fixed" || formValues.loanType === "Variable"
    ? formValues.loanType
    : "Fixed";

  // Map loan type based on program
  const mortgageTypes: string[] = [];
  const qmTypes: string[] = [];
  const ausList: string[] = [];
  const searchFilter: { programNames: string[] } = { programNames: [] };
  let subordinateLoanAmount = 0;
  let cltv = 0;

  switch (formValues.mortgageType) {
    case "Conventional":
      mortgageTypes.push("Conventional");
      if (formValues.loanType === "Fixed") {
        ausList.push("None", "GUS", "MUW", "LP", "DU");
        searchFilter.programNames.push("FNMA", "FHLMC");
      } else if (formValues.loanType === "HomeReady") {
        ausList.push("None", "GUS", "MUW", "DU");
        searchFilter.programNames.push("HomeReady");
      } else if (formValues.loanType === "HomePossible") {
        ausList.push("None", "GUS", "MUW", "LP");
        searchFilter.programNames.push("Home Possible");
      } else {
        ausList.push("None", "GUS", "MUW", "LP", "DU");
      }
      qmTypes.push("QM");
      break;
    case "Non-QM":
      mortgageTypes.push("Conventional");
      if (formValues.citizenship === "ITIN") {
        searchFilter.programNames.push("ITIN");
      } else {
        searchFilter.programNames.push("NQHEM");
      }
      qmTypes.push("NONQM");
      //ausList.push("None", "GUS", "MUW", "LP", "DU");
      ausList.push("None");
      break;
    case "Non-QM/Second Lien":
      subordinateLoanAmount = loanAmount;
      cltv = ltv;
      appraisedValue = Math.ceil((loanAmount * 2) / ltv);

      mortgageTypes.push("Conventional");
      if (formValues.citizenship === "ITIN") {
        searchFilter.programNames.push("ITIN");
      } else {
        searchFilter.programNames.push("the2nd");
      }
      qmTypes.push("NONQM");
      //ausList.push("None", "GUS", "MUW", "LP", "DU");
      ausList.push("None");
      break;
    case "FHA":
      ausList.push("None", "GUS", "MUW", "LP", "DU");
      mortgageTypes.push("FHA");
      qmTypes.push("QM");
      break;
    case "theNada (DPA)":
      mortgageTypes.push("FHA");
      qmTypes.push("QM");
      ausList.push("None", "GUS", "MUW", "LP", "DU");
      break;
    case "VA":
      mortgageTypes.push("VA");
      qmTypes.push("QM");
      ausList.push("None", "GUS", "MUW", "LP", "DU");
      break;
    case "USDA":
      mortgageTypes.push("UsdaRural");
      qmTypes.push("QM");
      ausList.push("GUS", "MUW");
      break;
    case "HELOC":
      mortgageTypes.push("HELOC");
      qmTypes.push("QM");
      ausList.push("None", "GUS", "MUW", "LP", "DU");
      break;
    default:
      mortgageTypes.push("Conventional");
      qmTypes.push("QM");
      ausList.push("None", "GUS", "MUW", "LP", "DU");
      break;
  }

  let dscrRatio: number | undefined = undefined;
  let dscrDynamicRatio: string | undefined = undefined;

  switch (formValues.incomeType) {
    case "DSCR":
      dscrRatio = Number(formValues.dscrValue) || 0;
      dscrDynamicRatio = formValues.dscrValue;
      searchFilter.programNames = ["NONI", "SuperNONI"];
      mortgageTypes.push("BP");
      break;
    case "DSCR-AssetUtilization":
      // SuperNONI asset depletion is 1.15+ only — no user-entered DSCR
      dscrRatio = 1.15;
      dscrDynamicRatio = "DSCR>=1.15";
      searchFilter.programNames = ["SuperNONI"];
      mortgageTypes.push("BP");
      break;
  }

  /* 
  Credit Event:
          N/A: None - Not Applicable
          84+ Months: None - Not Applicable
          48+ Months: Foreclosure - 49-60 Months
          36 - 48 Months: Foreclosure - 37-48 Months
          24 - 35 Months: Foreclosure - 25-36 Months
          12 - 23 Months: Foreclosure - 13-24 Months

  const commonCreditEvent = [
          { value: "NA", label: "N/A", default: true },
          { value: "84+ Months", label: "84+ Months" },
          { value: "48+ Months", label: "48+ Months" },
          { value: "36 - 48 Months", label: "36 - 48 Months" },
          { value: "24 - 35 Months", label: "24 - 35 Months" },
          { value: "12 - 23 Months", label: "12 - 23 Months" },
];*/
  let Global_FORECLOSURES = "FC_Null";
  switch (formValues.creditEvent) {
    case "NA":
      break;
    case "84+ Months":
      Global_FORECLOSURES = "FC_7yr";
      break;
    case "48+ Months":
      Global_FORECLOSURES = "FC_4yr";
      break;
    case "36 - 48 Months":
      Global_FORECLOSURES = "FC_3yr";
      break;
    case "24 - 35 Months":
      Global_FORECLOSURES = "FC_2yr";
      break;
    case "12 - 23 Months":
      Global_FORECLOSURES = "FC_1yr";
      break;
  }

  let PrepayTerm = null;
  let PrepayType = null;
  let PrePayment_Plan_Type = null;
  let Global_PREPAYMENTPENALTY = false;

  console.log("formValues.prepaymentPenalty", formValues.prepaymentPenalty);

  switch (formValues.prepaymentPenalty) {
    case "5YR PPP 5% Fixed":
      PrepayTerm = "60 Months";
      PrepayType = "Flat";
      PrePayment_Plan_Type = "Fixed5";
      Global_PREPAYMENTPENALTY = true;
      break;
    case "4YR PPP 5% Fixed":
      PrepayTerm = "48 Months";
      PrepayType = "Flat";
      PrePayment_Plan_Type = "Fixed5";
      Global_PREPAYMENTPENALTY = true;
      break;
    case "3YR PPP 5% Fixed":
      PrepayTerm = "36 Months";
      PrepayType = "Flat";
      PrePayment_Plan_Type = "Fixed5";
      Global_PREPAYMENTPENALTY = true;
      break;
    case "2YR PPP 5% Fixed":
      PrepayTerm = "24 Months";
      PrepayType = "Flat";
      PrePayment_Plan_Type = "Fixed5";
      Global_PREPAYMENTPENALTY = true;
      break;
    case "1YR PPP 5% Fixed":
      PrepayTerm = "12 Months";
      PrepayType = "Flat";
      PrePayment_Plan_Type = "Fixed5";
      Global_PREPAYMENTPENALTY = true;
      break;
    case "5YR Declining PPP":
      PrepayTerm = "60 Months";
      PrepayType = "Tiered";
      PrePayment_Plan_Type = "StepDown";
      Global_PREPAYMENTPENALTY = true;
      break;
    case "4YR Declining PPP":
      PrepayTerm = "48 Months";
      PrepayType = "Tiered";
      PrePayment_Plan_Type = "StepDown";
      Global_PREPAYMENTPENALTY = true;
      break;
    case "3YR Declining PPP":
      PrepayTerm = "36 Months";
      PrepayType = "Tiered";
      PrePayment_Plan_Type = "StepDown";
      Global_PREPAYMENTPENALTY = true;
      break;
    case "2YR Declining PPP":
      PrepayTerm = "24 Months";
      PrepayType = "Tiered";
      PrePayment_Plan_Type = "StepDown";
      Global_PREPAYMENTPENALTY = true;
      break;
    case "5YR PPP 3% Fixed":
      PrepayTerm = "60 Months";
      PrepayType = "Flat";
      PrePayment_Plan_Type = "Fixed3";
      Global_PREPAYMENTPENALTY = true;
      break;
    case "4YR PPP 3% Fixed":
      PrepayTerm = "48 Months";
      PrepayType = "Flat";
      PrePayment_Plan_Type = "Fixed3";
      Global_PREPAYMENTPENALTY = true;
      break;
    case "3YR PPP 3% Fixed":
      PrepayTerm = "36 Months";
      PrepayType = "Flat";
      PrePayment_Plan_Type = "Fixed3";
      Global_PREPAYMENTPENALTY = true;
      break;
    case "2YR PPP 3% Fixed":
      PrepayTerm = "24 Months";
      PrepayType = "Flat";
      PrePayment_Plan_Type = "Fixed3";
      Global_PREPAYMENTPENALTY = true;
      break;
    case "1YR PPP 3% Fixed":
      PrepayTerm = "12 Months";
      PrepayType = "Flat";
      PrePayment_Plan_Type = "Fixed3";
      Global_PREPAYMENTPENALTY = true;
      break;
    case "5YR PPP 6mo Int":
      PrepayTerm = "60 Months";
      PrepayType = "6MosInt";
      PrePayment_Plan_Type = "6MosInt";
      Global_PREPAYMENTPENALTY = true;
      break;
    case "4YR PPP 6mo Int":
      PrepayTerm = "48 Months";
      PrepayType = "6MosInt";
      PrePayment_Plan_Type = "6MosInt";
      Global_PREPAYMENTPENALTY = true;
      break;
    case "3YR PPP 6mo Int":
      PrepayTerm = "36 Months";
      PrepayType = "6MosInt";
      PrePayment_Plan_Type = "6MosInt";
      Global_PREPAYMENTPENALTY = true;
      break;
    case "2YR PPP 6mo Int":
      PrepayTerm = "24 Months";
      PrepayType = "6MosInt";
      PrePayment_Plan_Type = "6MosInt";
      Global_PREPAYMENTPENALTY = true;
      break;
    case "1YR PPP 6mo Int":
      PrepayTerm = "12 Months";
      PrepayType = "6MosInt";
      PrePayment_Plan_Type = "6MosInt";
      Global_PREPAYMENTPENALTY = true;
      break;
    case "No PPP":
      PrepayTerm = "None";
      PrepayType = null;
      PrePayment_Plan_Type = null;
      Global_PREPAYMENTPENALTY = false;
      break;
  }

  // Determine monthly income based on loan type and state
  let monthlyIncome = 16000; // Default income
  if (
    formValues.loanType === "HomeReady" ||
    formValues.loanType === "HomePossible"
  ) {
    const stateConfig = getStateConfigByAbbreviation(formValues.state);
    if (stateConfig) {
      monthlyIncome = stateConfig.hrHpIncome;
      console.log(
        `🔍 [DEBUG] Using HomeReady/HomePossible income for ${formValues.state}: ${monthlyIncome}`
      );
    } else {
      console.log(
        `⚠️ [DEBUG] State config not found for ${formValues.state}, using default income: ${monthlyIncome}`
      );
    }
  }

  // Determine varLoanTypeCriteria based on amortizationType
  let varLoanTypeCriteria: string[] = [];
  switch (formValues.amortizationType) {
    case "30-7-6-ARM":
    case "40-7-6-ARM":
      varLoanTypeCriteria = ["_7_6"];
      break;
    case "0-10-6-ARM":
    case "30-10-6-ARM":
      varLoanTypeCriteria = ["_10_6"];
      break;
    default:
      varLoanTypeCriteria = [];
      break;
  }

  // Cap brokerCompensationValue to the percent that yields maxAmount if needed
  let cappedBrokerCompPercent = formValues.brokerCompensationValue;
  if (
    formValues.brokerCompensation === "LPC" &&
    typeof formValues.loanAmount === "number" &&
    typeof formValues.brokerCompensationValue === "number"
  ) {
    const maxAmount = 20000; // or get from config if dynamic
    const calculatedAmount =
      (formValues.brokerCompensationValue / 100) * formValues.loanAmount;
    if (calculatedAmount > maxAmount) {
      // Calculate the percent that yields exactly maxAmount, floored to 3 decimal digits
      const rawPercent = (maxAmount / formValues.loanAmount) * 100;
      cappedBrokerCompPercent = Math.floor(rawPercent * 1000) / 1000;
    }
  }

  const qmRequest = {
    companyId: "", // Added on the backend
    filter: searchFilter,
    criteria: {
      subordinateLoanAmount: subordinateLoanAmount,
      cltv: cltv,
      compensationType:
        formValues.brokerCompensation === "BPC"
          ? "BorrowerCompPlan"
          : "LenderCompPlan",
      loanAmount: loanAmount,
      purchasePrice: appraisedValue,
      appraisedValue: appraisedValue,
      monthlyIncome: monthlyIncome,
      // citizenshipType:
      //   formValues.citizenship === "US Citizen" ? "USCitizen" : "USCitizen", //TODO: Add the correct citizenship type
      ...(formValues.incomeType?.includes("DSCR")
        ? {}
        : { clientDti: Number(formValues.dti) }),
      ltv: ltv,
      monthlyDebt: monthlyIncome * Number(formValues.dti),
      escrowWaiver: Boolean(formValues.escrowWaiver),
      lenderFeeWaiver: Boolean(formValues.lenderFeeBuyOut),
      fico: Number(formValues.fico) || 740,
      firstTimeHomeBuyer: Boolean(formValues.firstTimeHomeBuyer),
      fundingFeeFinanced:
        formValues.mortgageType === "VA"
          ? formValues.guranteeFeeFinanced
          : null,
      guranteeFeeFinanced:
        formValues.mortgageType === "USDA"
          ? formValues.guranteeFeeFinanced
          : null,
      ufmipFinanced:
        formValues.mortgageType === "FHA"
          ? formValues.fundingFeeFinanced
          : null,
      loanPurpose:
        formValues.mortgageType === "VA" &&
        formValues.purpose === "StreamLineRefinance"
          ? "VAIRRRL"
          : formValues.purpose,
      loanType: loanType,
      mortgageTypes: mortgageTypes,
      pmiType: formValues.pmiType || "BPMI",
      propertyUse: formValues.occupancy,
      interestOnly: formValues.interestOnly ? "true" : "false",
      dscr: dscrRatio,
      vaUsageType: formValues.entitlementUsage ? "Subsequent_Use" : "First_Use",
      ...(formValues.mortgageType === "VA" && formValues.veteranType
        ? { veteranType: formValues.veteranType }
        : {}),
    },
    loanPurposeCriteria: [
      formValues.mortgageType === "VA" &&
      formValues.purpose === "StreamLineRefinance"
        ? "VAIRRRL"
        : formValues.purpose,
    ],
    loanTypeCriteria: [loanType],
    maxListingPerRate: -1,
    miCriteria: {
      miCompanyName: formValues.miCompanyName || null,
      miPremiumFinancedIndicator: formValues.miPremiumFinancedIndicator,
    },
    property: {
      address: {
        state: formValues.state!,
        zip: formValues.zipCode!,
      },
      numberOfUnit: Number.parseInt(formValues.numberOfUnits),
      propertyType:
        formValues.propertyType === "SFR Rural"
          ? "SingleFamily"
          : formValues.propertyType === "2-8 Mixed Use"
          ? "MultiFamily"
          : formValues.propertyType === "Non-Warrantable Condo"
          ? "Condos"
          : formValues.propertyType,
    },
    termsCriteria: [loanTerm],
    termsinMonths: false,
    brokerCriteria: {
      ausList: ausList,
      dayLocks: Number.parseInt(formValues.lockPeriod),
      compPlan: cappedBrokerCompPercent === 0 ? null : -cappedBrokerCompPercent,
      ...(qmTypes.length > 0 ? { qmTypes } : {}),
    },
    accessCriteria: {
      userContext: null,
    },
    affordableHousingType: ["HomeReady", "HomePossible"].includes(
      formValues.loanType
    )
      ? formValues.loanType
      : null,
    dayLocksCriteria: [Number.parseInt(formValues.lockPeriod)],
    dynamicPropertiesMap: {
      // Add rental term for DSCR loans with Investment occupancy
      ...(formValues.incomeType?.startsWith("DSCR") &&
      formValues.occupancy === "Investment" &&
      formValues.rentalTerm
        ? {
            AddlOccupancyType: {
              fieldId: "AddlOccupancyType",
              value: formValues.rentalTerm,
            },
          }
        : {}),
      InterestOnly: {
        fieldId: "InterestOnly",
        value: formValues.interestOnly ? "true" : "false",
      },
      GLOBAL_CONDOTYPE: {
        fieldId: "GLOBAL_CONDOTYPE",
        value: formValues.propertyType === "CondoTel" ? "Condo_tel" : null,
      },
      Global_nonwarrantable: {
        fieldId: "Global_nonwarrantable",
        value:
          formValues.propertyType === "Non-Warrantable Condo"
            ? "true"
            : "false",
      },
      HousingHistory: {
        fieldId: "HousingHistory",
        value: formValues.mortgageHistory,
      },
      Citizenship: {
        fieldId: "Citizenship",
        value: formValues.citizenship,
      },
      CompensationType: {
        fieldId: "CompensationType",
        value:
          formValues.brokerCompensation === "BPC"
            ? "BorrowerCompPlan"
            : "LenderCompPlan",
      },
      IncomeDocType: {
        fieldId: "IncomeDocType",
        value: formValues.incomeType?.includes("DSCR")
          ? "DSCR"
          : formValues.incomeType,
      },
      MORT30LATESLAST12M: {
        fieldId: "MORT30LATESLAST12M",
        value: formValues.mortgageHistory === "0x60x12" ? "1" : "0",
      },
      MORT60LATESLAST12M: {
        fieldId: "MORT60LATESLAST12M",
        value: formValues.mortgageHistory === "0x90x12" ? "1" : "0",
      },
      MORT90LATESLAST12M: {
        fieldId: "MORT90LATESLAST12M",
        value: "0",
      },
      MORT120LATESLAST12M: {
        fieldId: "MORT120LATESLAST12M",
        value: "0",
      },
      GLOBAL_BorrowerType: {
        fieldId: "GLOBAL_BorrowerType",
        value: formValues.vesting,
      },
      GLOBAL_MixedUse: {
        fieldId: "GLOBAL_MixedUse",
        value: formValues.propertyType === "2-8 Mixed Use" ? "true" : "false",
      },
      Global_RURAL: {
        fieldId: "Global_RURAL",
        // value: formValues.propertyType === "SingleFamily" ? "true" : "false",
      },
      FirstTimeInvestor: {
        fieldId: "FirstTimeInvestor",
        value: formValues.investorExperience === "true" ? "true" : "false",
      },
      ...(dscrDynamicRatio !== undefined
        ? {
            DSCRRATIO: {
              fieldId: "DSCRRATIO",
              value: dscrDynamicRatio,
            },
          }
        : {}),
      PrepayTerm: { fieldId: "PrepayTerm", value: PrepayTerm },
      PrepayType: { fieldId: "PrepayType", value: PrepayType },
      PrePayment_Plan_Type: {
        fieldId: "PrePayment_Plan_Type",
        value: PrePayment_Plan_Type,
      },
      GLOBAL_ACHPayment: {
        fieldId: "GLOBAL_ACHPayment",
        value: formValues.achWaiver ? "false" : "true",
      },
      Global_PREPAYMENTPENALTY: {
        fieldId: "Global_PREPAYMENTPENALTY",
        value: Global_PREPAYMENTPENALTY.toString(),
      },
      // Add rental term for DSCR loans with Investment occupancy
      ...(formValues.incomeType?.startsWith("DSCR") &&
      formValues.occupancy === "Investment" &&
      formValues.rentalTerm
        ? {
            RentalTerm: {
              fieldId: "RentalTerm",
              value: formValues.rentalTerm,
            },
          }
        : {}),
      //From here are the default values
      GLOBAL_GIFTFUNDPERCENT: {
        fieldId: "GLOBAL_GIFTFUNDPERCENT",
        value: "0",
      },
      GLOBAL_MortgageLoanChargeOffs: {
        fieldId: "GLOBAL_MortgageLoanChargeOffs",
        value: "MLCONone",
      },
      Global_FORECLOSURES: {
        fieldId: "Global_FORECLOSURES",
        value: Global_FORECLOSURES,
      },
      Global_SHORTSALES: {
        fieldId: "Global_SHORTSALES",
        value: "SS_Null",
      },
      Global_DEEDINLIEU: {
        fieldId: "Global_DEEDINLIEU",
        value: "DIL_Null",
      },
      GLOBAL_Forbearances: {
        fieldId: "GLOBAL_Forbearances",
        value: "ForbearNone",
      },
      GLOBAL_RESERVES: {
        fieldId: "GLOBAL_RESERVES",
        // value: "Reserves_24",
      },
    },
    skipAdjustments: false,
    varLoanTypeCriteria: varLoanTypeCriteria,
    showDisqualify: true,
    showDisqualifyRules: true,
    showUnmatchCompPlan: true,
    dynaToSmo: true,
  };

  debug.trace("mapFormToQMPricingRequest", "Final request", {
    requestState: qmRequest.property.address.state,
    fullRequest: qmRequest,
  });

  return qmRequest as unknown as QMPricingRequest;
}

// Convert QMPricingResponse to PricingResult[] grouped by program
export function convertQMResponseToPricingResults(
  response: QMPricingResponse,
  prepaymentPenalty?: string,
  isNonQMSearch: boolean = false,
  formValues?: any
): {
  programName: string;
  results: PricingResult[];
  isDisqualified: boolean;
}[] {
  try {
    if (!response || !response.resultMap || response.resultMap.length === 0) {
      return [
        {
          programName: "No Results",
          isDisqualified: false,
          results: [
            {
              baseRate: 0,
              finalRate: 0,
              adjustments: [],
              alternativeProgram:
                "No results found. Please check your search criteria.",
              totalPayment: 0,
              loanType: "",
              amortizationType: "",
              loanTerm: "",
              lockPeriod: 0,
              price: 0,
            },
          ],
        },
      ];
    }

    // Collect all products from all resultMap elements
    const allProducts: PricingResult[] = [];

    response.resultMap.forEach((resultMap) => {
      if (!resultMap.resultRates || !resultMap.resultRates.data) {
        return;
      }

      const { rateSet, data } = resultMap.resultRates;
      if (!Array.isArray(rateSet) || rateSet.length === 0) {
        return;
      }

      // Process all rates in rateSet
      for (const rate of rateSet) {
        const rateStr = rate.toString();
        // Try multiple string formats to handle different API response formats
        let rateData = data[rateStr];

        // If not found, try with one decimal place (e.g., "7.0")
        if (!rateData) {
          rateData = data[rate.toFixed(1)];
        }

        // If still not found, try with three decimal places (e.g., "7.000")
        if (!rateData) {
          rateData = data[rate.toFixed(3)];
        }

        if (!rateData || !Array.isArray(rateData) || rateData.length === 0) {
          allProducts.push({
            price: 0,
            baseRate: rate,
            finalRate: rate,
            adjustments: [],
            alternativeProgram: "No data available for this rate",
            totalPayment: 0,
            loanType: "",
            amortizationType: "",
            loanTerm: "",
            lockPeriod: 0,
          });
          continue;
        }

        // Process ALL products in the rateData array
        for (const product of rateData) {
          // Only filter out NonCompliant if NOT a NonQM search
          if (!isNonQMSearch && product.compliancesMapByType?.QM) {
            const hasNonCompliant = product.compliancesMapByType.QM.some(
              (compliance) => compliance.severity === "NonCompliant"
            );
            if (hasNonCompliant) {
              // Skip this product as it's non-compliant
              console.log(
                `🔍 [DEBUG] Filtering out non-compliant product: ${product.programName} (${product.baseRates}%)`
              );
              continue;
            }
          }

          // Extract disqualifyAdjustments from groupAdjustmentProperties
          const disqualifyAdjustments: string[] = [];
          // Collect all adjustment details from groupAdjustmentProperties
          const allAdjustments: {
            name: string;
            type: string;
            value: number;
          }[] = [];

          product.groupAdjustmentProperties.forEach((group) => {
            if (
              group.disqualifyAdjustments &&
              group.disqualifyAdjustments.length > 0
            ) {
              disqualifyAdjustments.push(...group.disqualifyAdjustments);
            }

            // Collect adjustment details from each group
            if (group.adjustments && group.adjustments.length > 0) {
              group.adjustments.forEach((adjustment) => {
                if (adjustment.key) {
                  allAdjustments.push({
                    name: adjustment.key,
                    type: adjustment.type,
                    value: adjustment.adj,
                  });
                }
              });
            }
          });

          // Filter out theBlanket programs
          if (
            product.programName &&
            product.programName.includes("theBlanket")
          ) {
            console.log(
              `🔍 [DEBUG] Filtering out theBlanket program: ${product.programName}`
            );
            continue;
          }

          // Filter out Foreign National programs when citizenship is not "Foreign National"
          if (
            formValues &&
            formValues.citizenship !== "Foreign National" &&
            product.disqualified &&
            disqualifyAdjustments.length > 0
          ) {
            const isForeignNationalProgram =
              (product.programName &&
                (product.programName.includes("Foreign National") ||
                  product.programName.includes("FN"))) ||
              disqualifyAdjustments.some((reason) =>
                reason.includes("Foreign Nationals Only")
              );

            if (isForeignNationalProgram) {
              console.log(
                `🔍 [DEBUG] Filtering out Foreign National program: ${product.programName} (citizenship: ${formValues.citizenship})`
              );
              continue;
            }
          }

          // Create base adjustments from API response
          const baseAdjustments = product.groupAdjustmentProperties.map(
            (adj: AdjustmentGroup) => ({
              name: adj.name,
              value: adj.totalAdjustment,
            })
          );

          // Add ACH Waiver adjustment if applicable
          const adjustments = [...baseAdjustments];
          let updatedAllAdjustments = [...allAdjustments];
          // if (formValues?.achWaiver === true) {
          //   // Check if this is an eligible program (not disqualified)
          //   if (!product.disqualified) {
          //     adjustments.push({
          //       name: "ACH Waiver",
          //       value: -0.25,
          //     });
          //     updatedAllAdjustments.push({
          //       name: "ACH Waiver",
          //       type: "LLPA",
          //       value: -0.25,
          //     });
          //   }

          //   product.adjustedPoints = product.adjustedPoints + 0.25;
          // }

          allProducts.push({
            baseRate: product.baseRates,
            finalRate: product.adjustedRates,
            adjustments: adjustments,
            alternativeProgram: product.programName,
            totalPayment:
              product.monthlyPayment.monthlyPI ?? product.monthlyPayment.total,
            loanType: product.loanType,
            amortizationType: product.productName
              .toLowerCase()
              .includes("fixed")
              ? "Fixed"
              : "ARM",
            loanTerm: product.term.toString(),
            lockPeriod: product.dayLock,
            apr: product.apr,
            monthlyPayment: product.monthlyPayment,
            borrowerPaid: product.borrowerPaid,
            finalClosingCost: product.finalClosingCost,
            borrowerPaidDetails: product.borrowerPaidDetails,
            disqualified: product.disqualified,
            disqualifyAdjustments: disqualifyAdjustments,
            allAdjustments: updatedAllAdjustments,
            price: 100 - (product.adjustedPoints ?? 0),
          });
        }
      }
    });

    // Group products by program name and disqualification status
    const programGroups = new Map<
      string,
      {
        programName: string;
        results: PricingResult[];
        isDisqualified: boolean;
      }
    >();

    allProducts.forEach((product) => {
      const isDisqualified = Boolean(
        product.disqualified ||
          (product.disqualifyAdjustments &&
            product.disqualifyAdjustments.length > 0)
      );

      // Create separate groups for qualified and disqualified products of the same program
      const groupKey = `${product.alternativeProgram}_${
        isDisqualified ? "disqualified" : "qualified"
      }`;

      if (!programGroups.has(groupKey)) {
        programGroups.set(groupKey, {
          programName: product.alternativeProgram,
          results: [],
          isDisqualified: isDisqualified,
        });
      }

      programGroups.get(groupKey)!.results.push(product);
    });

    // Convert map to array and sort results within each group
    const groupedResults = Array.from(programGroups.values()).map((group) => {
      // Sort by rate (lowest first) and then by price (lowest first)
      const sortedResults = group.results.sort((a, b) => {
        if (a.finalRate !== b.finalRate) {
          return a.finalRate - b.finalRate;
        }
        return a.price - b.price;
      });

      // Filter out rates with the same price but higher rates
      const filteredResults: PricingResult[] = [];
      const priceMap = new Map<number, PricingResult>();

      for (const result of sortedResults) {
        const price = result.price;
        const existingResult = priceMap.get(price);

        if (!existingResult) {
          // First time seeing this price, add it
          priceMap.set(price, result);
          filteredResults.push(result);
        } else if (result.finalRate < existingResult.finalRate) {
          // Found a better rate for the same price, replace it
          const index = filteredResults.indexOf(existingResult);
          if (index !== -1) {
            filteredResults[index] = result;
            priceMap.set(price, result);
          }
        }
        // If we have the same price but higher rate, skip it
      }

      return {
        ...group,
        results: filteredResults,
      };
    });

    // Sort groups: qualified programs first, then disqualified programs
    // Within each category, sort by program name
    groupedResults.sort((a, b) => {
      if (a.isDisqualified !== b.isDisqualified) {
        return a.isDisqualified ? 1 : -1; // Qualified first
      }
      return a.programName.localeCompare(b.programName);
    });

    // --- CUSTOM LOGIC FOR NONI PPP ELIGIBILITY ---
    // if (prepaymentPenalty) {
    //   // Remove programs based on prepaymentPenalty selection
    //   const lowerPenalty = prepaymentPenalty.toLowerCase();
    //   let filteredResults = groupedResults;
    //   if (lowerPenalty.includes("5% fixed")) {
    //     filteredResults = filteredResults.filter(
    //       (group) => !group.programName.toLowerCase().includes("declining")
    //     );
    //   } else if (lowerPenalty.includes("declining")) {
    //     filteredResults = filteredResults.filter(
    //       (group) => !group.programName.toLowerCase().includes("5%")
    //     );
    //   }

    //   const isEligiblePPP =
    //     prepaymentPenalty.includes("5% Fixed") ||
    //     prepaymentPenalty.includes("Declining PPP");
    //   filteredResults.forEach((group) => {
    //     if (
    //       group.programName &&
    //       group.programName.toUpperCase().includes("NONI")
    //     ) {
    //       if (!isEligiblePPP) {
    //         group.results.forEach((result) => {
    //           // Only move to disqualified if not already disqualified
    //           if (!result.disqualified) {
    //             result.disqualified = true;
    //             if (!result.disqualifyAdjustments)
    //               result.disqualifyAdjustments = [];
    //             result.disqualifyAdjustments.push(
    //               "PPP option not available for NONI."
    //             );
    //           }
    //         });
    //         group.isDisqualified = true;
    //       }
    //     }
    //   });
    //   return filteredResults;
    // }
    // --- END CUSTOM LOGIC ---

    return groupedResults;
  } catch (err) {
    console.error("Error converting QM response:", err);
    return [
      {
        programName: "Error",
        isDisqualified: false,
        results: [
          {
            price: 0,
            baseRate: 0,
            finalRate: 0,
            adjustments: [],
            alternativeProgram:
              "An unexpected error occurred. Please try again or contact support.",
            totalPayment: 0,
            loanType: "",
            amortizationType: "",
            loanTerm: "",
            lockPeriod: 0,
          },
        ],
      },
    ];
  }
}

export async function fetchPricingData(
  formValues: any,
  signal?: AbortSignal,
  onRequest?: (reqObj: any) => void,
  onRawResponse?: (rawResp: any) => void
): Promise<
  { programName: string; results: PricingResult[]; isDisqualified: boolean }[]
> {
  console.log(
    "🔍 [DEBUG] Starting fetchPricingData with form values:",
    formValues
  );

  try {
    // Convert form values to QMPricingRequest
    const qmRequest = mapFormToQMPricingRequest(formValues);
    console.log("🔍 [DEBUG] Converted to QM request:", qmRequest);
    if (onRequest) onRequest(qmRequest);

    let results: {
      programName: string;
      results: PricingResult[];
      isDisqualified: boolean;
    }[] = [];
    let totalAdjustment = 0;
    let debugAdjustments: any = {};

    try {
      await debug.performance("API Request", async () => {
        console.log(
          "🔍 [DEBUG] Making API request to:",
          config.api.endpoints.pricing
        );

        const response = await fetch(config.api.endpoints.pricing, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(qmRequest),
          signal,
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(
            "❌ [DEBUG] API request failed:",
            response.status,
            response.statusText,
            errorText
          );
          throw new Error(
            `HTTP error! status: ${response.status}, message: ${errorText}`
          );
        }

        const data = await response.json();
        if (onRawResponse) onRawResponse(data);
        console.log("🔍 [DEBUG] Raw API response:", data);

        // Determine if this is a NonQM search
        const isNonQMSearch = Array.isArray(formValues?.mortgageType)
          ? formValues.mortgageType.includes("Non-QM") ||
            formValues.mortgageType.includes("Non-QM/Second Lien")
          : formValues?.mortgageType === "Non-QM" ||
            formValues?.mortgageType === "Non-QM/Second Lien";

        results = convertQMResponseToPricingResults(
          data,
          formValues.prepaymentPenalty,
          isNonQMSearch,
          formValues
        );
        console.log("🔍 [DEBUG] Converted results:", results);
      });
    } catch (error: unknown) {
      console.error("❌ [DEBUG] Error in fetchPricingData:", error);
      debugAdjustments.error = {
        message:
          error instanceof Error ? error.message : "Unknown error occurred",
        stack: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString(),
      };

      if (error instanceof Error && error.name === "AbortError") {
        throw error;
      }
      // Return fallback grouped structure with error info
      results = [
        {
          programName: "Error",
          isDisqualified: false,
          results: [
            {
              baseRate: 0,
              finalRate: 0,
              adjustments: [],
              alternativeProgram:
                "An error occurred while fetching pricing data. Please try again.",
              totalPayment: 0,
              loanType: "",
              amortizationType: "",
              loanTerm: "",
              lockPeriod: 0,
              price: 0,
            },
          ],
        },
      ];
    }

    return results;
  } catch (error: unknown) {
    console.error("❌ [DEBUG] Error in fetchPricingData:", error);
    if (error instanceof Error && error.name === "AbortError") {
      throw error;
    }
    // Return fallback grouped structure with error info
    return [
      {
        programName: "Error",
        isDisqualified: false,
        results: [
          {
            baseRate: 0,
            finalRate: 0,
            adjustments: [],
            alternativeProgram:
              "An error occurred while fetching pricing data. Please try again.",
            totalPayment: 0,
            loanType: "",
            amortizationType: "",
            loanTerm: "",
            lockPeriod: 0,
            price: 0,
          },
        ],
      },
    ];
  }
}
